import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { AnimatePresence } from 'motion/react';
import { ChevronRightIcon, MagnifyingGlassIcon, Squares2X2Icon } from '@heroicons/react/24/outline';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Textarea } from '@oysterjs/ui/textarea';

import { DataTable, type ColumnDef } from '@oysterjs/ui/data-table';

import {
  type Role,
  type AgencyProfileCapability,
  type CustomPresetFieldsFragment,
  type TeamFieldsFragment
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { PinnedTray, UseCaseBrowser } from '~/components/home/use-case-browser';
import { useCaseById } from '~/components/home/use-case-catalog';
import { type HomePin } from '~/api/data/home/use-pinned-home-items';
import {
  applyCapabilityToggle,
  applyNavigationToggle,
  assignableAgencyUsers,
  capabilitySummary,
  grantsCapability,
  profileGoverningUser,
  reachableNavigation,
  toggleInList,
  userExplicitlyOnProfile,
  type AgencyProfileAssignment,
  type AgencyProfileFormInput,
  type CapabilityCatalog,
  type CapabilityGroup,
  type CapabilityOption,
  type CapabilitySection,
  withDesignWording
} from './agency-profile-form-input';

type UpdateForm = (updater: (prev: AgencyProfileFormInput) => AgencyProfileFormInput) => void;

type SectionProps = {
  form: AgencyProfileFormInput;
  updateForm: UpdateForm;
};

/**
 * The two-column shell every settings section on this page uses: purpose on the
 * left, controls on the right. Matches the voice agent sections so Profiles
 * reads as part of the same settings area.
 */
const AgencyProfileSection = ({
  title,
  description,
  children
}: {
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="px-4 py-6 sm:px-6">
      <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
        <div>
          <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">{title}</h4>
          <div className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">{description}</div>
        </div>
        <div className="col-span-2 min-w-0">
          <Fieldset className="flex min-w-0 flex-col gap-y-6">{children}</Fieldset>
        </div>
      </div>
    </div>
  </div>
);

export const AgencyProfileBasicInformationSection = ({ form, updateForm }: SectionProps) => (
  <AgencyProfileSection
    title="Basic Information"
    description="What this profile is called and whether this is the default profile applied to unassigned users."
  >
    <FieldGroup>
      <Field className="max-w-md">
        <Label>Name</Label>
        <Description>A short, descriptive name for this profile.</Description>
        <Input
          value={form.name}
          placeholder="Producer"
          onChange={(e) => {
            const name = e.currentTarget.value;
            updateForm((prev) => ({ ...prev, name }));
          }}
        />
      </Field>
      <Field className="max-w-md">
        <Label>Description</Label>
        <Description>Who this profile is for. Only admins see this.</Description>
        <Textarea
          rows={3}
          value={form.description}
          placeholder="Sales-facing view for producers who quote and bind."
          onChange={(e) => {
            const description = e.currentTarget.value;
            updateForm((prev) => ({ ...prev, description }));
          }}
        />
      </Field>
      <SwitchField>
        <Switch
          color="sky"
          checked={form.isDefault}
          onChange={(isDefault) => updateForm((prev) => ({ ...prev, isDefault }))}
        />
        <Label>Default profile</Label>
        <Description>
          Users who have not been assigned a profile get this one. Only one profile can be the
          default, so turning this on takes it from whichever profile is currently the default.
        </Description>
      </SwitchField>
    </FieldGroup>
  </AgencyProfileSection>
);

type AssignmentUser = { id: string; name: string; email: string; roles: Role[] };

/**
 * One selectable row, in the shape both tables below share: a name over a
 * detail line, and a badge on the right saying why the row is the way it is.
 */
type AssignmentRow = {
  id: string;
  name: string;
  detail: string;
  badge?: { label: string; color: 'sky' | 'zinc' };
  selected: boolean;
  /** Set when the row follows from a choice made elsewhere and cannot be ticked here. */
  lockedReason?: string;
  /**
   * Haystack for the search box, when the row is findable by more than what it
   * shows — a person is reasonably searched for by their team, which appears
   * only as a badge.
   */
  searchText?: string;
};

/**
 * A searchable, fixed-height picker. Both halves of Assignment are the same
 * table with different rows, so they stay the same height, the same density,
 * and the same distance from their search box.
 */
const AssignmentTable = ({
  rows,
  searchPlaceholder,
  emptyLabel,
  maxHeight,
  onToggle
}: {
  rows: AssignmentRow[];
  searchPlaceholder: string;
  emptyLabel: string;
  maxHeight: number;
  onToggle: (id: string) => void;
}) => {
  const [query, setQuery] = React.useState('');

  const terms = query.trim().toLowerCase();
  const matched = terms
    ? rows.filter((row) =>
        (row.searchText ?? `${row.name} ${row.detail}`).toLowerCase().includes(terms)
      )
    : rows;

  const toggle = (row: AssignmentRow) => {
    if (row.lockedReason) {
      return;
    }
    onToggle(row.id);
  };

  const columns: ColumnDef<AssignmentRow>[] = [
    {
      id: 'selected',
      header: '',
      headerClassName: 'w-8',
      cellClassName: 'py-1.5 pl-3 pr-0 align-middle',
      noHref: true,
      renderCell: (row) => (
        <Checkbox
          color="sky"
          aria-label={row.name}
          title={row.lockedReason}
          disabled={!!row.lockedReason}
          checked={row.selected}
          onChange={() => toggle(row)}
        />
      )
    },
    {
      id: 'name',
      header: 'Name',
      renderCell: (row) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-neutral-900 dark:text-white">{row.name}</div>
          <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {row.detail}
          </div>
        </div>
      )
    },
    {
      id: 'status',
      header: 'Status',
      headerClassName: 'text-right',
      cellClassName: 'py-1.5 px-3 text-right',
      renderCell: (row) =>
        row.badge ? <Badge color={row.badge.color}>{row.badge.label}</Badge> : null
    }
  ];

  return (
    <>
      <div className="mt-2">
        <InputGroup>
          <MagnifyingGlassIcon />
          <Input
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </InputGroup>
      </div>
      <div className="mt-3 overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
        {matched.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {terms ? 'Nothing matches your search.' : emptyLabel}
          </p>
        ) : (
          <DataTable
            data={matched}
            columns={columns}
            keyExtractor={(row) => row.id}
            rowClassName="cursor-pointer"
            onRowClick={toggle}
            stickyHeader
            scrollable
            maxHeight={maxHeight}
          />
        )}
      </div>
    </>
  );
};

/**
 * Counts the people a profile reaches, deduplicated: someone on two selected
 * teams, or on a team and named individually, is one person.
 */
const reachOf = (
  form: AgencyProfileFormInput,
  teams: TeamFieldsFragment[]
): { viaTeams: Set<string>; individual: string[] } => {
  const viaTeams = new Set<string>();
  for (const team of teams) {
    if (!form.teamIds.includes(team.id)) {
      continue;
    }
    for (const member of team.members) {
      viaTeams.add(member.user.id);
    }
  }
  return { viaTeams, individual: form.userIds.filter((id) => !viaTeams.has(id)) };
};

export const AgencyProfileAssignmentSection = ({
  form,
  updateForm,
  profileId,
  teams,
  agencyUsers,
  agencyProfileAssignments
}: SectionProps & {
  profileId: string;
  teams: TeamFieldsFragment[];
  agencyUsers: AssignmentUser[];
  agencyProfileAssignments: AgencyProfileAssignment[];
}) => {
  const assignableUsers = React.useMemo(() => assignableAgencyUsers(agencyUsers), [agencyUsers]);

  const { viaTeams, individual } = reachOf(form, teams);
  const reach = viaTeams.size + individual.length;

  const teamsOfUser = React.useMemo(() => {
    const byUser = new Map<string, TeamFieldsFragment[]>();
    for (const team of teams) {
      for (const member of team.members) {
        byUser.set(member.user.id, [...(byUser.get(member.user.id) ?? []), team]);
      }
    }
    return byUser;
  }, [teams]);

  const teamIdsOfUser = React.useMemo(() => {
    const byUser = new Map<string, Set<string>>();
    for (const team of teams) {
      for (const member of team.members) {
        const ids = byUser.get(member.user.id) ?? new Set<string>();
        ids.add(team.id);
        byUser.set(member.user.id, ids);
      }
    }
    return byUser;
  }, [teams]);

  const teamRows: AssignmentRow[] = teams.map((team) => ({
    id: team.id,
    name: team.name,
    detail: `${team.memberCount} ${team.memberCount === 1 ? 'member' : 'members'}`,
    badge: form.teamIds.includes(team.id) ? { label: 'Assigned', color: 'sky' } : undefined,
    selected: form.teamIds.includes(team.id)
  }));

  const userRows: AssignmentRow[] = assignableUsers.map((user) => {
    const grantingTeams = (teamsOfUser.get(user.id) ?? []).filter((team) =>
      form.teamIds.includes(team.id)
    );
    // A team-covered row is a consequence of the team table above, so it reads
    // as ticked and locked rather than as something to untick here — unticking
    // it would silently do nothing.
    const covered = grantingTeams.length > 0;

    const governingProfile = profileGoverningUser(
      user.id,
      agencyProfileAssignments,
      teamIdsOfUser.get(user.id) ?? new Set()
    );
    const userTeamIds = teamIdsOfUser.get(user.id) ?? new Set();
    const coveredByOtherProfile =
      governingProfile != null &&
      governingProfile.id !== profileId &&
      userExplicitlyOnProfile(user.id, governingProfile, userTeamIds) &&
      !form.userIds.includes(user.id);
    const grantingProfileTeams = coveredByOtherProfile
      ? (teamsOfUser.get(user.id) ?? []).filter((team) =>
          governingProfile!.teams.some((profileTeam) => profileTeam.id === team.id)
        )
      : [];

    return {
      id: user.id,
      name: user.name || user.email,
      detail: user.email,
      badge: covered
        ? { label: `via team ${grantingTeams[0].name}`, color: 'sky' }
        : coveredByOtherProfile
          ? { label: `on profile ${governingProfile!.name}`, color: 'sky' }
          : form.userIds.includes(user.id)
            ? { label: 'Assigned', color: 'zinc' }
            : undefined,
      selected: covered || form.userIds.includes(user.id),
      lockedReason: covered
        ? `Covered by ${grantingTeams.map((team) => team.name).join(', ')} — remove the team to remove them`
        : coveredByOtherProfile
          ? grantingProfileTeams.length > 0
            ? `Covered by ${governingProfile!.name} via ${grantingProfileTeams.map((team) => team.name).join(', ')} — remove the team from that profile to assign here`
            : `On ${governingProfile!.name} — remove them from that profile to assign here`
          : undefined,
      searchText: [
        user.name,
        user.email,
        ...(teamsOfUser.get(user.id) ?? []).map((t) => t.name),
        governingProfile?.name
      ]
        .filter(Boolean)
        .join(' ')
    };
  });

  return (
    <AgencyProfileSection
      title="Assignment"
      description={
        <>
          <p className="m-0 text-sm/6">
            Users to whom this profile is applied. A user belongs to one profile at a time, so
            assigning them here moves them from their current profile.
          </p>
          <p className="mt-3 text-sm/6">
            People often sit on more than one team. Anyone matched by any selected team is counted
            once, and their row shows which team is granting them access.
          </p>
          <p className="mt-3 text-sm/6">
            Agency admins are not listed. Profiles never apply to them, so that they can always
            reach the settings that produced one.
          </p>
        </>
      }
    >
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 px-4 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tabular-nums text-neutral-900 dark:text-white">
            {reach}
          </span>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {reach === 1 ? 'person uses' : 'people use'} this profile
          </span>
        </div>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {reach === 0
            ? form.isDefault
              ? 'Nobody is assigned directly. As the default profile, this applies to every unassigned user.'
              : 'Nobody is assigned yet, so this profile is not in use.'
            : `${viaTeams.size} via teams · ${individual.length} individually`}
        </p>
      </div>

      <Field>
        <Label>Teams</Label>
        <Description>
          {form.teamIds.length} of {teams.length} selected. Everyone on a selected team gets this
          profile.
        </Description>
        <AssignmentTable
          rows={teamRows}
          searchPlaceholder="Search teams"
          emptyLabel="No teams yet. Create one under Settings › Teams to assign a whole group at once."
          maxHeight={224}
          onToggle={(id) =>
            updateForm((prev) => ({ ...prev, teamIds: toggleInList(prev.teamIds, id) }))
          }
        />
      </Field>

      <Field>
        <Label>People added directly</Label>
        <Description>
          {teams.length > 0
            ? individual.length > 0
              ? `${individual.length} added here, on top of the ${viaTeams.size} the teams above already cover.`
              : 'Add people who need this profile but are not on one of the teams above.'
            : individual.length > 0
              ? `${individual.length} added here.`
              : 'Add people who need this profile.'}
        </Description>
        <AssignmentTable
          rows={userRows}
          searchPlaceholder="Search name, email, or team"
          emptyLabel="Nobody to assign yet."
          maxHeight={288}
          onToggle={(id) =>
            updateForm((prev) => ({ ...prev, userIds: toggleInList(prev.userIds, id) }))
          }
        />
      </Field>
    </AgencyProfileSection>
  );
};

export const AgencyProfileHomeScreenSection = ({
  form,
  updateForm,
  customPresets,
  hasProAccess
}: SectionProps & { customPresets: CustomPresetFieldsFragment[]; hasProAccess: boolean }) => {
  const [browserOpen, setBrowserOpen] = React.useState(false);

  const presetsByID = React.useMemo(
    () => new Map(customPresets.map((preset) => [preset.id, preset])),
    [customPresets]
  );

  // The stored pins are the wire shape; the browser speaks HomePin, so the two
  // are converted at this boundary rather than duplicating the tray's model.
  const pins = React.useMemo<HomePin[]>(
    () =>
      form.pins.flatMap<HomePin>((pin) => {
        if (pin.useCase) {
          return [{ kind: 'useCase', id: pin.useCase }];
        }
        return pin.customPresetId ? [{ kind: 'customPreset', id: pin.customPresetId }] : [];
      }),
    [form.pins]
  );

  const setPins = React.useCallback(
    (next: HomePin[]) =>
      updateForm((prev) => ({
        ...prev,
        pins: next.map((pin) =>
          pin.kind === 'useCase' ? { useCase: pin.id } : { customPresetId: pin.id }
        )
      })),
    [updateForm]
  );

  const titleOf = React.useCallback(
    (pin: HomePin) =>
      pin.kind === 'useCase'
        ? (useCaseById(pin.id)?.title ?? pin.id)
        : (presetsByID.get(pin.id)?.title ?? pin.id),
    [presetsByID]
  );

  const samePin = (a: HomePin, b: HomePin) => a.kind === b.kind && a.id === b.id;

  return (
    <>
      <AgencyProfileSection
        title="Home Screen"
        description="The first thing these users see. Pin the presets they should be able to run and configure their home screen."
      >
        <FieldGroup>
          <SwitchField>
            <Switch
              color="sky"
              checked={form.askCaraEnabled}
              onChange={(askCaraEnabled) => updateForm((prev) => ({ ...prev, askCaraEnabled }))}
            />
            <Label>Show Ask Cara</Label>
            <Description>
              The free-form input at the top of the home screen. Turn it off for users who should
              only run the presets below.
            </Description>
          </SwitchField>
          <SwitchField>
            <Switch
              color="sky"
              checked={form.allowUserCustomization}
              onChange={(allowUserCustomization) =>
                updateForm((prev) => ({ ...prev, allowUserCustomization }))
              }
            />
            <Label>Let users rearrange their home screen</Label>
            <Description>
              When on, the presets below are a starting point users can pin to and reorder. When
              off, the home screen is locked to what you set here.
            </Description>
          </SwitchField>
          <Field>
            <Label>Pinned presets</Label>
            <Description>
              Shown in this order. The first six fit without scrolling on most screens.
            </Description>
            <div className="mt-2 flex items-center justify-between gap-3">
              <Button color="white" onClick={() => setBrowserOpen(true)}>
                <Squares2X2Icon data-slot="icon" />
                Browse presets
              </Button>
            </div>
            <div className="mt-3">
              <PinnedTray
                pinned={pins}
                surfaceLabel="the home screen"
                titleOf={titleOf}
                onReorder={setPins}
                onRemove={(pin) => setPins(pins.filter((p) => !samePin(p, pin)))}
                expanded
              />
            </div>
          </Field>
        </FieldGroup>
      </AgencyProfileSection>

      <AnimatePresence mode="wait" initial>
        {browserOpen && (
          <UseCaseBrowser
            key="profile-use-case-browser"
            onClose={() => setBrowserOpen(false)}
            surfaceLabel="the home screen"
            pinned={pins}
            isPinned={(pin) => pins.some((p) => samePin(p, pin))}
            onTogglePin={(pin) =>
              setPins(
                pins.some((p) => samePin(p, pin))
                  ? pins.filter((p) => !samePin(p, pin))
                  : [...pins, pin]
              )
            }
            onRemovePin={(pin) => setPins(pins.filter((p) => !samePin(p, pin)))}
            onReorder={setPins}
            customPresets={customPresets}
            libraries={{}}
            hasProAccess={hasProAccess}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const SUMMARY_COLORS = {
  none: 'zinc',
  partial: 'sky',
  full: 'green'
} as const;

/** One capability: what it lets someone do, and what ticking it drags along. */
const CapabilityRow = ({
  form,
  updateForm,
  catalog,
  option
}: SectionProps & { catalog: CapabilityCatalog; option: CapabilityOption }) => {
  const granted = grantsCapability(form, option.capability);
  const missingRequirements = option.requires.filter(
    (capability) => !form.capabilities.includes(capability)
  );

  return (
    <CheckboxField className="min-w-0 mt-2">
      <Checkbox
        color="sky"
        checked={granted}
        onChange={() => updateForm((prev) => applyCapabilityToggle(prev, catalog, option))}
      />
      <Label>{option.label}</Label>
      <Description>
        {option.hint}
        {!granted && missingRequirements.length > 0 && (
          <span className="text-neutral-500 dark:text-neutral-400">
            {' '}
            Also turns on: {missingRequirements.map(capabilityLabel(catalog)).join(', ')}.
          </span>
        )}
      </Description>
    </CheckboxField>
  );
};

/** Reads a capability's label out of the catalog, for the "also turns on" line. */
const capabilityLabel = (catalog: CapabilityCatalog) => (capability: AgencyProfileCapability) =>
  catalog.groups
    .flatMap((group) => group.sections)
    .flatMap((section) => section.capabilities)
    .find((option) => option.capability === capability)?.label ?? capability;

/** One surface: what these users can do on it, and whether it appears in the sidebar. */
const CapabilitySectionRow = ({
  form,
  updateForm,
  catalog,
  section,
  expanded,
  onToggleExpanded
}: SectionProps & {
  catalog: CapabilityCatalog;
  section: CapabilitySection;
  expanded: boolean;
  onToggleExpanded: () => void;
}) => {
  const summary = capabilitySummary(form, section);
  const granted = section.capabilities.filter((option) =>
    form.capabilities.includes(option.capability)
  );
  const navigation = reachableNavigation(catalog, [
    ...granted.map((option) => option.capability)
  ]).filter((navigationOption) =>
    section.capabilities.some((option) =>
      option.navigation.some((item) => item.item === navigationOption.item)
    )
  );

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="flex min-w-0 items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="min-w-0 flex-1 overflow-hidden">
            <span className="block truncate text-sm font-medium text-neutral-900 dark:text-white">
              {section.title}
            </span>
            <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
              {section.hint}
            </span>
          </span>
          <Badge className="shrink-0" color={SUMMARY_COLORS[summary.tone]}>
            {summary.label}
          </Badge>
          <ChevronRightIcon
            aria-hidden="true"
            className={clsx(
              'size-4 shrink-0 text-neutral-400 dark:text-neutral-500',
              expanded && 'rotate-90'
            )}
          />
        </button>
      </div>

      {expanded && (
        <div className="min-w-0 space-y-6 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-3 py-3">
          {navigation.length > 0 && (
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Navigation
              </p>
              <CheckboxGroup className="mt-2">
                {navigation.map((option) => (
                  <CheckboxField key={option.item}>
                    <Checkbox
                      color="sky"
                      checked={form.navigation.includes(option.item)}
                      onChange={() => updateForm((prev) => applyNavigationToggle(prev, option))}
                    />
                    <Label>Show {option.label} in the sidebar</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
            </div>
          )}

          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              What they can do
            </p>
            {section.capabilities.map((option) => (
              <CapabilityRow
                key={option.capability}
                form={form}
                updateForm={updateForm}
                catalog={catalog}
                option={option}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AgencyProfileAccessSection = ({
  form,
  updateForm,
  catalog: servedCatalog
}: SectionProps & { catalog: CapabilityCatalog }) => {
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const catalog = React.useMemo(
    () => (designsOn ? withDesignWording(servedCatalog) : servedCatalog),
    [designsOn, servedCatalog]
  );
  // Keyed by group and section title together, since a section name like
  // "Clients" appears under more than one module.
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const sectionKey = (group: CapabilityGroup, section: CapabilitySection) =>
    `${group.title}/${section.title}`;

  const setAllExpanded = (open: boolean) =>
    setExpanded(
      open
        ? Object.fromEntries(
            catalog.groups.flatMap((group) =>
              group.sections.map((section) => [sectionKey(group, section), true])
            )
          )
        : {}
    );

  return (
    <AgencyProfileSection
      title="Access"
      description={
        <>
          <p className="m-0 text-sm/6">
            Grouped the way the app is: a module, the pages inside it, and what these users can do
            on each. Expand a page to set that, and whether it appears in their sidebar.
          </p>
          <p className="mt-3 text-sm/6">
            Options bring in what they depend on: turning on &ldquo;edit a preset&rdquo; turns on
            running them too, because an editor who cannot open a preset has nothing to edit.
            Turning an option off takes away anything that needed it. A profile only ever narrows,
            so nothing here grants more than the person&rsquo;s role already allows.
          </p>
          <div className="mt-3 flex items-center gap-4">
            <Headless.Button
              className="text-sm font-medium text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"
              onClick={() => setAllExpanded(true)}
            >
              Expand all
            </Headless.Button>
            <Headless.Button
              className="text-sm font-medium text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"
              onClick={() => setAllExpanded(false)}
            >
              Collapse all
            </Headless.Button>
          </div>
        </>
      }
    >
      <div className="min-w-0 space-y-6">
        {catalog.groups.map((group) => {
          return (
            <div key={group.title} className="min-w-0">
              <div className="flex min-w-0 items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <h5 className="m-0 text-xs font-medium uppercase text-neutral-500 dark:text-neutral-400">
                    {group.title}
                  </h5>
                </div>
              </div>
              <div className="mt-2 min-w-0 space-y-2">
                {group.sections.map((section) => (
                  <CapabilitySectionRow
                    key={section.title}
                    form={form}
                    updateForm={updateForm}
                    catalog={catalog}
                    section={section}
                    expanded={!!expanded[sectionKey(group, section)]}
                    onToggleExpanded={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [sectionKey(group, section)]: !prev[sectionKey(group, section)]
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AgencyProfileSection>
  );
};
